const SLOT_START_HOUR = 8;
const SLOT_END_HOUR = 20;
const SLOT_INTERVAL_MINUTES = 30;

export interface CallbackSlot {
  label: string;
  timeLabel: string;
  targetTime: string;
}

const pad = (value: number): string => String(value).padStart(2, '0');

export const formatNaiveLocalTime = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): string => `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:00`;

export const getZonedParts = (date: Date, timezone: string) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parseInt(parts.find((part) => part.type === type)?.value ?? '0', 10);

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
  };
};

/**
 * Finds the UTC instant for a wall-clock time in an IANA timezone.
 */
export const localTimeToUtcDate = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timezone: string,
): Date => {
  let ts = Date.UTC(year, month - 1, day, hour, minute, 0);

  for (let attempt = 0; attempt < 6; attempt++) {
    const parts = getZonedParts(new Date(ts), timezone);

    if (
      parts.year === year &&
      parts.month === month &&
      parts.day === day &&
      parts.hour === hour &&
      parts.minute === minute
    ) {
      return new Date(ts);
    }

    const dayDiff =
      Date.UTC(parts.year, parts.month - 1, parts.day) -
      Date.UTC(year, month - 1, day);
    const minuteDiff = dayDiff / (24 * 60 * 60 * 1000) * 24 * 60 +
      (hour - parts.hour) * 60 +
      (minute - parts.minute);

    ts += minuteDiff * 60_000;
  }

  return new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
};

const formatTimeLabel = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timezone: string,
): string => {
  const slotDate = localTimeToUtcDate(year, month, day, hour, minute, timezone);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(slotDate);
};

const formatSlotLabel = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timezone: string,
  now: Date,
): string => {
  const slotDate = localTimeToUtcDate(year, month, day, hour, minute, timezone);
  const nowParts = getZonedParts(now, timezone);

  const isToday =
    year === nowParts.year && month === nowParts.month && day === nowParts.day;

  const tomorrowProbe = localTimeToUtcDate(
    nowParts.year,
    nowParts.month,
    nowParts.day + 1,
    12,
    0,
    timezone,
  );
  const tomorrowParts = getZonedParts(tomorrowProbe, timezone);
  const isTomorrow =
    year === tomorrowParts.year &&
    month === tomorrowParts.month &&
    day === tomorrowParts.day;

  const timeLabel = formatTimeLabel(year, month, day, hour, minute, timezone);

  if (isToday) return `Today, ${timeLabel}`;
  if (isTomorrow) return `Tomorrow, ${timeLabel}`;

  const dateLabel = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(slotDate);

  return `${dateLabel}, ${timeLabel}`;
};

/**
 * Returns a human-readable heading for the schedule date (e.g. "Tomorrow, Wed, May 28").
 */
export const getScheduleDateHeading = (
  slots: CallbackSlot[],
  timezone: string,
  now: Date = new Date(),
): string => {
  if (slots.length === 0) return 'Choose a callback time';

  const match = slots[0].targetTime.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (!match) return 'Choose a callback time';

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  const slotDate = localTimeToUtcDate(year, month, day, 12, 0, timezone);
  const nowParts = getZonedParts(now, timezone);

  const isToday =
    year === nowParts.year && month === nowParts.month && day === nowParts.day;

  const tomorrowParts = addCalendarDays(
    nowParts.year,
    nowParts.month,
    nowParts.day,
    1,
    timezone,
  );
  const isTomorrow =
    year === tomorrowParts.year &&
    month === tomorrowParts.month &&
    day === tomorrowParts.day;

  const dateLabel = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(slotDate);

  if (isToday) return `Today, ${dateLabel}`;
  if (isTomorrow) return `Tomorrow, ${dateLabel}`;
  return dateLabel;
};

const generateSlotsForDay = (
  year: number,
  month: number,
  day: number,
  timezone: string,
  now: Date,
): CallbackSlot[] => {
  const slots: CallbackSlot[] = [];

  for (let hour = SLOT_START_HOUR; hour <= SLOT_END_HOUR; hour++) {
    for (const minute of [0, SLOT_INTERVAL_MINUTES]) {
      if (hour === SLOT_END_HOUR && minute > 0) continue;

      const slotDate = localTimeToUtcDate(year, month, day, hour, minute, timezone);
      if (slotDate.getTime() <= now.getTime()) continue;

      slots.push({
        label: formatSlotLabel(year, month, day, hour, minute, timezone, now),
        timeLabel: formatTimeLabel(year, month, day, hour, minute, timezone),
        targetTime: formatNaiveLocalTime(year, month, day, hour, minute),
      });
    }
  }

  return slots;
};

const addCalendarDays = (
  year: number,
  month: number,
  day: number,
  daysToAdd: number,
  timezone: string,
): { year: number; month: number; day: number } => {
  const base = localTimeToUtcDate(year, month, day, 12, 0, timezone);
  const shifted = new Date(base.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
  return getZonedParts(shifted, timezone);
};

/**
 * Generates 30-minute callback slots from 8:00am–8:00pm in the given timezone.
 * Returns future slots for the first day that has availability.
 */
export const generateCallbackSlots = (timezone: string, now: Date = new Date()): CallbackSlot[] => {
  const today = getZonedParts(now, timezone);

  for (let dayOffset = 0; dayOffset < 14; dayOffset++) {
    const { year, month, day } = addCalendarDays(
      today.year,
      today.month,
      today.day,
      dayOffset,
      timezone,
    );
    const slots = generateSlotsForDay(year, month, day, timezone, now);

    if (slots.length > 0) {
      return slots;
    }
  }

  return [];
};
